import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, Box, Typography, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import WarningIcon from '@material-ui/icons/Warning';
import QRCode from 'qrcode';
import { t } from '../../i18n';

const APP_DOWNLOAD_URL = `${window.location.origin}/mobile/ecoogps-mobile.apk`;
// Versão do APK actualmente publicado — lida de um version.json servido
// ao lado do APK (escrito pelo processo de publicação). Assim o badge
// reflecte sempre a versão real sem editar este ficheiro a cada build.
const APP_VERSION_URL = `${window.location.origin}/mobile/version.json`;

const useStyles = makeStyles(() => ({
  paper: {
    background: 'linear-gradient(180deg, #ffffff 0%, #dbeafe 100%)',
    borderRadius: 20,
    border: '1px solid rgba(0,0,0,0.08)',
    maxWidth: 380,
    overflow: 'visible',
    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '36px 32px 28px !important',
    gap: 16,
  },
  iconWrap: {
    width: 52,
    height: 52,
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 6px 20px rgba(59,130,246,0.35)',
  },
  title: {
    color: '#1e293b',
    fontWeight: 700,
    fontSize: '1.1rem',
    textAlign: 'center',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '0.75rem',
    textAlign: 'center',
    lineHeight: 1.5,
  },
  qrBox: {
    background: '#ffffff',
    borderRadius: 14,
    padding: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    marginTop: 4,
  },
  instructions: {
    color: '#475569',
    fontSize: '0.78rem',
    textAlign: 'center',
    lineHeight: 1.6,
    fontWeight: 500,
  },
  warningBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    background: 'rgba(245,158,11,0.12)',
    border: '1px solid rgba(245,158,11,0.30)',
    borderRadius: 10,
    padding: '10px 14px',
    width: '100%',
  },
  warningIcon: {
    color: '#d97706',
    fontSize: 18,
    marginTop: 1,
    flexShrink: 0,
  },
  warningText: {
    color: '#5c3d0e',
    fontSize: '0.70rem',
    lineHeight: 1.5,
  },
  closeBtn: {
    position: 'absolute',
    top: 10,
    right: 10,
    color: 'rgba(0,0,0,0.3)',
    padding: 6,
    '&:hover': { color: '#1e293b', background: 'rgba(0,0,0,0.05)' },
  },
  versionBadge: {
    color: 'rgba(0,0,0,0.3)',
    fontSize: '0.62rem',
    letterSpacing: '0.06em',
    marginTop: 2,
  },
}));

function AppDownloadModal({ open, onClose, lang = 'pt' }) {
  const classes = useStyles();
  const canvasRef = useRef(null);
  const [qrReady, setQrReady] = useState(false);
  const [appVersion, setAppVersion] = useState(null);

  // Vai buscar a versão do APK publicado sempre que o modal abre.
  useEffect(() => {
    if (!open) return;
    fetch(`${APP_VERSION_URL}?t=${Date.now()}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.version) setAppVersion(d.version); })
      .catch(() => { /* mantém null — badge mostra fallback */ });
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQrReady(false);
      return;
    }
    // O QR só é gerado DEPOIS de termos a versão para meter como query
    // param ?v=1.6.16. Sem isto, browsers / Android package installer
    // cacheavam APKs antigas (mesmo path → reuso). Forçar URL diferente
    // por versão garante download fresh em cada bump.
    if (!appVersion) return;
    const downloadUrl = `${APP_DOWNLOAD_URL}?v=${encodeURIComponent(appVersion)}`;
    const timer = setTimeout(() => {
      if (canvasRef.current) {
        QRCode.toCanvas(canvasRef.current, downloadUrl, {
          width: 220,
          margin: 2,
          color: { dark: '#1e3a5f', light: '#ffffff' },
        }, (err) => {
          if (!err) setQrReady(true);
        });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [open, appVersion]);

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.paper }}>
      <Button size="small" onClick={onClose} startIcon={<CloseIcon style={{ fontSize: 14 }} />}
        style={{ position: 'absolute', top: 12, right: 12, zIndex: 1, fontSize: '0.72rem', color: '#64748b', textTransform: 'none', fontWeight: 600, borderRadius: 8, border: '1px solid #e2e8f0', padding: '4px 12px', background: '#fff' }}>
        Fechar
      </Button>
      <DialogContent className={classes.content}>
        <Box className={classes.iconWrap}>
          <PhoneAndroidIcon style={{ color: '#fff', fontSize: 26 }} />
        </Box>
        <Typography className={classes.title}>{t(lang, 'appDownloadTitle')}</Typography>
        <Box className={classes.qrBox} style={{ opacity: qrReady ? 1 : 0.3 }}>
          <canvas ref={canvasRef} />
        </Box>
        <Typography className={classes.instructions}>
          {t(lang, 'appDownloadScan')}
        </Typography>
        <Box className={classes.warningBox}>
          <WarningIcon className={classes.warningIcon} />
          <Typography className={classes.warningText}>
            {t(lang, 'appDownloadWarning')}
          </Typography>
        </Box>
        <Typography className={classes.versionBadge}>
          {appVersion ? `Versão ${appVersion}` : 'Versão —'}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}

export default AppDownloadModal;
